import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeIn } from "@/components/animated";
import { faqs } from "@/data/homePageData";
import { ChevronDown, HelpCircle } from "lucide-react";

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle }: FAQItemProps) {
  return (
    <motion.div
      initial={false}
      className="border-b border-secondary-200 last:border-0"
    >
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-center justify-between text-left group"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-secondary-900 group-hover:text-primary-600 transition-colors pr-4">
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <ChevronDown className="h-5 w-5 text-secondary-400 group-hover:text-primary-600 transition-colors" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-secondary-600 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-20 bg-white">
      <div className="container">
        <FadeIn>
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 text-primary-600 mb-4">
              <HelpCircle className="h-6 w-6" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Preguntas frecuentes
            </h2>
            <p className="text-secondary-600 max-w-2xl mx-auto">
              Resolvemos tus dudas para que puedas empezar a aprender con
              confianza
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.2}>
          <div className="max-w-3xl mx-auto bg-secondary-50 rounded-2xl p-6 md:p-8">
            {faqs.map((faq, index) => (
              <FAQItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onToggle={() =>
                  setOpenIndex(openIndex === index ? null : index)
                }
              />
            ))}
          </div>
        </FadeIn>

        <FadeIn delay={0.3}>
          <p className="text-center mt-8 text-secondary-500">
            ¿Tienes más preguntas?{" "}
            <a
              href="mailto:admin@arcuslearn.com"
              className="text-primary-600 hover:text-primary-700 font-medium underline-offset-2 hover:underline"
            >
              Contáctanos
            </a>
          </p>
        </FadeIn>
      </div>
    </section>
  );
}
